import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {Actions, ofAction, Select, Store} from '@ngxs/store';
import {SettingsState} from '../../state/settings.state';
import {Observable} from 'rxjs';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {Settings} from '../../actions/settings.actions';
import {AuthState} from '../../state/auth.state';
import {Auth} from '../../actions/auth.actions';
import {OrganizationActions} from '../../actions/organiztion.actions';
import {OrganizationState} from '../../state/organization.state';
import Organization from '../../models/organization/organization.model';
import {first, map, takeUntil} from 'rxjs/operators';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import UpdateDarkMode = Settings.UpdateDarkMode;
import GetOrganization = OrganizationActions.GetOrganization;
import UpdateOrganization = OrganizationActions.UpdateOrganization;
import UpdateStickyHeader = Settings.UpdateStickyHeader;

@Component({
  standalone: false,
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
  @Select(SettingsState.darkMode)
  public darkMode$: Observable<boolean>;

  @Select(SettingsState.stickyHeader)
  public stickyHeader$: Observable<boolean>;

  @Select(AuthState.isAuthenticated)
  public authenticated$: Observable<boolean>;

  @Select(AuthState.modules)
  public modules$: Observable<string[]>;

  @Select(AuthState.roles)
  public roles$: Observable<string[]>;

  @Select(OrganizationState.organization)
  public organization$: Observable<Organization>;

  public loadingIndicator: boolean;

  public isEditing: boolean;

  public organizationForm: UntypedFormGroup;

  constructor(private store: Store, private fb: UntypedFormBuilder, private actions: Actions) {

  }

  ngOnInit(): void {
    this.loadingIndicator = false;
    this.isEditing = false;

    this.authenticated$.pipe(
      first(val => val)
    ).subscribe(() => {
      this.loadingIndicator = true;
      this.store.dispatch([
        new GetOrganization(this.store.selectSnapshot(AuthState.organization))
      ]).subscribe(() => {
        this.loadingIndicator = false;
      });
    });

    this.organization$.pipe(takeUntil(this.actions.pipe(ofAction(Auth.ClearState)))).subscribe((org) => {
      if (org) {
        this.initForm(org);
      }
    });
  }

  initForm(organization: Organization): void {
    this.organizationForm = this.fb.group({
        name: [organization.name],
        description: [organization.description],
        region: [organization.region],
        currency: [organization.currency],
        defaultColor: [organization.defaultColor],
        bankDetails: [this.fb.group({
          bank: [organization.bankDetails?.bank],
          branchCode: [organization.bankDetails?.branchCode],
          accountNumber: [organization.bankDetails?.accountNumber],
          accountType: [organization.bankDetails?.accountType],
          swiftCode: [organization.bankDetails?.swiftCode],
        })],
        taxPercentage: [organization.taxPercentage],
        includeTax: [organization.includeTax],
        pickupAddress: [this.fb.group({
          line1: [organization.pickupAddress?.line1],
          line2: [organization.pickupAddress?.line2],
          complex: [organization.pickupAddress?.complex],
          suburb: [organization.pickupAddress?.suburb],
          city: [organization.pickupAddress?.city],
          state: [organization.pickupAddress?.state],
          country: [organization.pickupAddress?.country],
          postalCode: [organization.pickupAddress?.postalCode],
          type: ['DELIVERY'],
        })],
        billingAddress: [this.fb.group({
          line1: [organization.billingAddress?.line1],
          line2: [organization.billingAddress?.line2],
          complex: [organization.billingAddress?.complex],
          suburb: [organization.billingAddress?.suburb],
          city: [organization.billingAddress?.city],
          state: [organization.billingAddress?.state],
          country: [organization.billingAddress?.country],
          postalCode: [organization.billingAddress?.postalCode],
          type: ['BILLING'],
        })],
        fromEmail: [organization.fromEmail],
        inboxEmail: [organization.inboxEmail],
        phoneNumber: [organization.phoneNumber],
        invflowUrl: [organization.invflowUrl],
        logoUrl: [organization.logoUrl],
        website: [organization.website],
        resetPasswordUrl: [organization.resetPasswordUrl],
        cryptoKey: [organization.cryptoKey],
        cryptoIv: [organization.cryptoIv]
      }
    );
  }
  updateDarkMode($event: MatSlideToggleChange): void {
    this.store.dispatch(new UpdateDarkMode($event.checked));
  }

  updateStickyHeader($event: MatSlideToggleChange): void {
    this.store.dispatch(new UpdateStickyHeader($event.checked));
  }

  cancel(): void {
    const organization = this.store.selectSnapshot(OrganizationState.organization);
    this.initForm(organization);
    this.isEditing = false;
  }

  save(): void {
    const updateOrganization: Organization = {
      id: null,
      active: true,
      handle: null,
      documentTypes: [],
      name: this.organizationForm.get('name').value,
      description: this.organizationForm.get('description').value,
      region: this.organizationForm.get('region').value,
      currency: this.organizationForm.get('currency').value,
      defaultColor: this.organizationForm.get('defaultColor').value,
      bankDetails: {
        bank: this.organizationForm.get('bankDetails').value.get('bank').value,
        branchCode: this.organizationForm.get('bankDetails').value.get('branchCode').value,
        accountNumber: this.organizationForm.get('bankDetails').value.get('accountNumber').value,
        accountType: this.organizationForm.get('bankDetails').value.get('accountType').value,
        swiftCode: this.organizationForm.get('bankDetails').value.get('swiftCode').value,
      },
      taxPercentage: this.organizationForm.get('taxPercentage').value,
      includeTax: this.organizationForm.get('includeTax').value,
      pickupAddress: {
        line1: this.organizationForm.get('pickupAddress').value.get('line1').value,
        line2: this.organizationForm.get('pickupAddress').value.get('line2').value,
        complex: this.organizationForm.get('pickupAddress').value.get('complex').value,
        suburb: this.organizationForm.get('pickupAddress').value.get('suburb').value,
        city: this.organizationForm.get('pickupAddress').value.get('city').value,
        state: this.organizationForm.get('pickupAddress').value.get('state').value,
        country: this.organizationForm.get('pickupAddress').value.get('country').value,
        postalCode: this.organizationForm.get('pickupAddress').value.get('postalCode').value,
        type: 'DELIVERY',
        preferred: false
      },
      billingAddress: {
        line1: this.organizationForm.get('billingAddress').value.get('line1').value,
        line2: this.organizationForm.get('billingAddress').value.get('line2').value,
        complex: this.organizationForm.get('billingAddress').value.get('complex').value,
        suburb: this.organizationForm.get('billingAddress').value.get('suburb').value,
        city: this.organizationForm.get('billingAddress').value.get('city').value,
        state: this.organizationForm.get('billingAddress').value.get('state').value,
        country: this.organizationForm.get('billingAddress').value.get('country').value,
        postalCode: this.organizationForm.get('billingAddress').value.get('postalCode').value,
        type: 'BILLING',
        preferred: false
      },
      fromEmail: this.organizationForm.get('fromEmail').value,
      inboxEmail: this.organizationForm.get('inboxEmail').value,
      phoneNumber: this.organizationForm.get('phoneNumber').value,
      invflowUrl: this.organizationForm.get('invflowUrl').value,
      logoUrl: this.organizationForm.get('logoUrl').value,
      website: this.organizationForm.get('website').value,
      resetPasswordUrl: this.organizationForm.get('resetPasswordUrl').value,
      cryptoKey: this.organizationForm.get('cryptoKey').value,
      cryptoIv: this.organizationForm.get('cryptoIv').value
    };
    const id: string = this.store.selectSnapshot(OrganizationState.organization).id;

    this.loadingIndicator = true;
    this.store.dispatch([new UpdateOrganization(id, updateOrganization)]).subscribe(() => {
      this.loadingIndicator = false;
      this.isEditing = false;
    });
  }

  edit(): void {
    this.isEditing = true;
  }

  hasRoleAsync(role: string): Observable<boolean> {
    return this.roles$.pipe(
      map(roles => roles.includes(role))
    );
  }
}
